import { RenderFormat, ViewerDocumentKind, ViewerDownloadTargetKind, ViewerOpenContextKind, type ContentViewerSession } from '@/content-viewer/store/schemas'
import { createViewerSession } from '@/content-viewer/utils/create-viewer-session'
import { MimeType } from '@/constants/constants-mime-types'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'
import { getAgencyOriginalFormat } from '@/agent-v2/agency-documents/utils/agency-original-format'
import { AnalyticsReferenceType, type AnalyticsReference } from '@/analytics/schemas/analytics-reference.schemas'
import { getAgentV2ReferenceDisplayName } from '@/agent-v2/utils/agent-v2-reference-displays'

export function buildAgentV2ReferenceViewerSession(reference: AgentV2Reference, turnId = reference.turn_id): ContentViewerSession | null {
  const context = {
    kind: ViewerOpenContextKind.CHAT_CITATION,
    // The reference UUID (never the ref_ token): the viewer uses it as its remount key.
    referenceId: reference.reference_id,
    citationId: reference.citation_id,
    turnId,
    conversationId: reference.conversation_id,
    analyticsReference: buildAgentV2AnalyticsReference(reference),
  } as const

  switch (reference.source_type) {
    case 'web':
      // Web sources open at their original public URL, not in a stored-document viewer.
      return null
    case 'docket':
      // The saved record is the reference itself; the viewer rebuilds it from the
      // conversation's references, so the session carries identity only.
      return createViewerSession({
        document: {
          kind: ViewerDocumentKind.DOCKET,
          conversationId: reference.conversation_id,
          citationId: reference.citation_id,
          title: getAgentV2ReferenceDisplayName(reference),
        },
        context,
      })
    case 'administrative_decision': {
      // Only a stored PDF, plain-text, or Markdown original opens in-app; every other format is an inactive citation.
      const metadata = reference.source.document_metadata
      if (!getAgencyOriginalFormat(metadata.media_type) || !metadata.artifact_object_uri || !metadata.artifact_sha256) return null
      return createViewerSession({
        document: {
          kind: ViewerDocumentKind.AGENCY,
          conversationId: reference.conversation_id,
          referenceId: reference.reference_id,
          title: getAgentV2ReferenceDisplayName(reference),
          downloadTarget: {
            kind: ViewerDownloadTargetKind.AGENCY_ORIGINAL,
            conversationId: reference.conversation_id,
            referenceId: reference.reference_id,
          },
        },
        context,
      })
    }
    case 'caselaw': {
      const storagePath = reference.source.s3_path
      if (!storagePath) return null

      const title = getAgentV2ReferenceDisplayName(reference)

      return createViewerSession({
        document: {
          kind: ViewerDocumentKind.CASELAW,
          s3Path: storagePath,
          parentId: reference.source.parent_id,
          title,
          caseStatus: reference.case_status,
          renderAsset: {
            format: RenderFormat.HTML,
            sourceKind: ViewerDocumentKind.CASELAW,
            storagePath,
          },
          downloadTarget: {
            kind: ViewerDownloadTargetKind.RICH_REFERENCE_DOCX,
            sourceKind: ViewerDocumentKind.CASELAW,
            storagePath,
            fileName: reference.source.title || 'source',
          },
        },
        context,
        presentation: {
          markdownHighlights: reference.support.selected_sentences,
        },
      })
    }

    case 'lrr': {
      const storagePath = reference.source.s3_dir
      const title = getAgentV2LrrTitle(reference.source)

      return createViewerSession({
        document: {
          kind: ViewerDocumentKind.LRR,
          s3Path: storagePath,
          title,
          contentStatus: reference.content_status,
          renderAsset: {
            format: RenderFormat.HTML,
            sourceKind: ViewerDocumentKind.LRR,
            storagePath,
          },
          downloadTarget: {
            kind: ViewerDownloadTargetKind.RICH_REFERENCE_DOCX,
            sourceKind: ViewerDocumentKind.LRR,
            storagePath,
            fileName: title || 'source',
          },
        },
        context,
        presentation: {
          markdownHighlights: reference.support.selected_sentences,
        },
      })
    }

    case 'matter_file': {
      const viewableGcsPath = reference.source.viewable_gcs_path
      const viewableContentType = reference.source.viewable_content_type
      if (!viewableGcsPath || !isSupportedMatterFileContentType(viewableContentType)) return null

      const format = getMatterFileRenderFormat(viewableContentType)
      // page_nums are one-based document pages, as the file index stores them; the viewer
      // navigates by zero-based page index, so a citation of page 7 must open index 6.
      const citedPage = reference.source.page_nums[0]
      const startingPage = citedPage !== undefined && citedPage >= 1 ? citedPage - 1 : undefined

      return createViewerSession({
        document: {
          kind: ViewerDocumentKind.USER_FILE,
          userFileId: reference.source.matter_file_id,
          title: reference.source.filename,
          renderAsset: {
            format,
          },
          downloadTarget: {
            kind: ViewerDownloadTargetKind.USER_FILE_ORIGINAL,
            userFileId: reference.source.matter_file_id,
          },
        },
        context,
        presentation: {
          ...(format === RenderFormat.MARKDOWN ? { markdownHighlights: reference.support.selected_sentences } : {}),
          ...(format === RenderFormat.PDF && reference.support.highlight_areas?.length ? { pdfHighlights: reference.support.highlight_areas } : {}),
          ...(format === RenderFormat.PDF && !reference.support.highlight_areas?.length ? { markdownHighlights: reference.support.selected_sentences } : {}),
          ...(format === RenderFormat.PDF && startingPage !== undefined ? { startingPage } : {}),
        },
      })
    }

    case 'living_asset': {
      // The reference identifies the durable source only — a file id, pages, and support
      // evidence. No renderAsset: the viewer resolves PDF vs markdown by id at open time,
      // so a PDF source applies the stored page and geometry while a non-PDF source renders
      // its processed markdown and highlights the source-backed support sentences.
      // page_nums are already zero-based page indexes.
      const startingPage = reference.source.page_nums[0]

      return createViewerSession({
        document: {
          kind: ViewerDocumentKind.USER_FILE,
          userFileId: reference.source.matter_file_id,
          title: reference.source.filename,
          downloadTarget: {
            kind: ViewerDownloadTargetKind.USER_FILE_ORIGINAL,
            userFileId: reference.source.matter_file_id,
          },
        },
        context,
        presentation: {
          // Both presentations travel together; the renderer applies the one its resolved
          // format understands (geometry for PDF, sentence matching for markdown).
          ...(reference.support.highlight_areas?.length ? { pdfHighlights: reference.support.highlight_areas } : {}),
          markdownHighlights: reference.support.selected_sentences,
          ...(startingPage !== undefined ? { startingPage } : {}),
        },
      })
    }
  }
}

function buildAgentV2AnalyticsReference(reference: AgentV2Reference): AnalyticsReference {
  const metadata =
    reference.source_type === 'lrr'
      ? {
          ...reference.source,
          title: getAgentV2LrrTitle(reference.source),
        }
      : reference.source

  return {
    reference_number: reference.citation_id,
    text: reference.support.summary,
    metadata: {
      ...metadata,
      citation_id: reference.citation_id,
      reference_type: getAgentV2AnalyticsReferenceType(reference.source_type),
    },
    relevant_sentences: reference.support.selected_sentences,
    relevant_highlights: reference.support.highlight_areas,
  }
}

function getAgentV2AnalyticsReferenceType(sourceType: AgentV2Reference['source_type']): string {
  switch (sourceType) {
    case 'lrr':
      return AnalyticsReferenceType.lrr_v3
    case 'matter_file':
    case 'living_asset':
      return 'user_file'
    default:
      return sourceType
  }
}

function getAgentV2LrrTitle(source: Extract<AgentV2Reference, { source_type: 'lrr' }>['source']): string {
  return source.title ?? source.long_title ?? source.legislation_name
}

function isSupportedMatterFileContentType(contentType: string | null): contentType is 'application/pdf' | 'text/markdown' {
  return contentType === MimeType.PDF || contentType === MimeType.MARKDOWN
}

function getMatterFileRenderFormat(contentType: 'application/pdf' | 'text/markdown'): RenderFormat.PDF | RenderFormat.MARKDOWN {
  if (contentType === MimeType.MARKDOWN) {
    return RenderFormat.MARKDOWN
  }

  return RenderFormat.PDF
}
